import { useCallback, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import {
  useUpdate,
  useNotify,
  SimpleForm,
  Edit,
  useResourceContext,
  required,
  maxLength,
  useRefresh,
  minLength,
  TextInput,
  useTranslate,
  ReferenceInput,
  AutocompleteInput,
  useRecordContext,
  useDataProvider,
} from "react-admin";
import { useFormContext } from "react-hook-form";
import MuiDatePickerInput from "../../common/MuiDatePickerInput";
import SaveDeleteBtns from "../../saveDeleteBtns/SaveDeleteBtn";
import SaleReturnItemForm from "./components/SaleReturnItemForm";
import RadioGroupX from "../common/RadioGroupX";
import currentDate from "../../../utils/currentDate";
import {
  OptionRendererCustomer,
  inputTextCustomer,
  matchSuggestionCustomer,
  filterToQueryCustomer,
} from "../../common/OptionRendererCustomer";
import CodeNumberInput from "../../common/CodeNumberInput";

const validateNumber = [required(), minLength(13), maxLength(13)];
const validateDate = [required(), minLength(10), maxLength(10)];
const validateCustomer = [required(), minLength(36), maxLength(36)];
// const validateNote = [maxLength(100)];

const SaleItemHydrator = () => {
  const record = useRecordContext();
  const { getValues, setValue } = useFormContext();
  const dataProvider = useDataProvider();

  useEffect(() => {
    let ignore = false;
    const saleItems = record?.sale_return_items ?? [];
    if (saleItems.length === 0) {
      return undefined;
    }

    saleItems.forEach((item, index) => {
      const source = `sale_return_items.${index}.sale_item.sale_id`;
      const saleId = item?.sale_item?.sale_id || item?.sale_item?.sale?.id;

      if (saleId) {
        if (getValues(source) !== saleId) {
          setValue(source, saleId, {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
          });
        }
        return;
      }

      const saleItemId = item?.sale_item_id || item?.sale_item?.id;
      if (!saleItemId) return;

      dataProvider
        .getOne("sale-items", { id: saleItemId })
        .then(({ data }) => {
          if (ignore || !data?.sale_id) return;
          if (getValues(source) !== data.sale_id) {
            setValue(source, data.sale_id, {
              shouldDirty: false,
              shouldTouch: false,
              shouldValidate: false,
            });
          }
        })
        .catch(() => {});
    });

    return () => {
      ignore = true;
    };
  }, [record, dataProvider, getValues, setValue]);

  return null;
};

export default function SaleReturnEditDialog({ open, setOpen, recordId }) {
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const resource = useResourceContext(); // cuman nama resource
  const translate = useTranslate();

  const save = useCallback(
    async (values) => {
      try {
        await update(
          resource,
          { id: values.id, data: values },
          {
            returnPromise: true,
            onSuccess: (data) => {
              notify("ra.notification.updated", {
                messageArgs: { smart_count: 1 },
              });
              setOpen(false);
              refresh();
            },
          }
        );
      } catch (error) {
        if (error.body && error.body.errors) {
          if (error.body.errors === "403") {
            // munculkan notifikasi erro ketika sudah approved
            notify("mine.notification.403_error", { type: "error" });
          } else {
            // The shape of the returned validation errors must match the shape of the form
            return error.body.errors;
          }
        }
      }
    },
    [update]
  );

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      keepMounted={false}
      fullWidth={true}
      maxWidth={false}
    >
      <Edit
        title={" "}
        id={recordId}
        actions={false}
        sx={{ "& .RaEdit-main": { mt: 0 } }}
      >
        <SimpleForm
          warnWhenUnsavedChanges
          onSubmit={save}
          toolbar={<SaveDeleteBtns callback={handleClose} />}
          mode="onBlur"
          reValidateMode="onBlur"
        >
          <SaleItemHydrator />
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 0,
            }}
          >
            <DialogTitle>
              {translate("mine.dialog.edit_sale_return")}
            </DialogTitle>
            <IconButton onClick={handleClose} sx={{ mr: "11px" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent sx={{ alignSelf: "normal" }}>
            <TextInput source="id" sx={{ display: "none" }} />

            <CodeNumberInput
              // open={open}
              dependOn="date"
              variant="outlined"
              source="code"
              label="mine.label.receipt_no"
              fullWidth
              validate={validateNumber}
              disabled
            />
            <MuiDatePickerInput
              variant="outlined"
              source="date"
              label="mine.label.date"
              fullWidth
              isRequired
              validate={validateDate}
              defaultValue={currentDate()}
            />

            <ReferenceInput
              source="customer_id"
              reference="customers"
              perPage={10}
              sort={{ field: "customers.name", order: "ASC" }}
            >
              <AutocompleteInput
                variant="outlined"
                fullWidth
                validate={validateCustomer}
                label="mine.label.customer"
                // optionText="code"
                optionText={<OptionRendererCustomer />}
                inputText={inputTextCustomer}
                matchSuggestion={matchSuggestionCustomer}
                filterToQuery={filterToQueryCustomer}
              />
            </ReferenceInput>

            <TextInput
              variant="outlined"
              source="note"
              label="mine.label.note"
              fullWidth
              multiline
              rows={3}
              // validate={validateNote}
            />

            <RadioGroupX />

            <Box
              sx={{
                  maxHeight: '300px', // Sesuaikan tinggi maksimum sesuai kebutuhan Anda
                  overflowY: 'auto',
                  border: '1px solid #ccc', // Opsional: untuk visualisasi batas
                  borderRadius: '4px',
                  p: 1, // Opsional: padding
              }}
            >
              <SaleReturnItemForm />
            </Box>
          </DialogContent>
        </SimpleForm>
      </Edit>
    </Dialog>
  );
}
